import { toDateString } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useConversations } from 'chat/conversations/queries/list'
import { useMembers } from 'chat/members/queries/list'
import { useRecipients } from 'chat/members/queries/recipients'
import {
  useFileDetails,
  useFolderDetails,
} from 'file_folder_repository/queries/details'
import {
  useShareFile,
  useShareFolder,
} from 'file_folder_repository/queries/share'
import { ShareResourceDetailsScreenProps } from 'home/stack/types'
import moment from 'moment'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { useUser } from 'users/queries/detail'
import { useShareResourceContext } from '../context'
import { ShareResourceRecipient } from '../types'
import { AcknowledgementType, ShareResourceDetailsView } from './views'

export function ShareResourceDetailsScreen({
  navigation,
  route,
}: ShareResourceDetailsScreenProps) {
  const { requestAcknowledgement, conversationId } = route.params
  const [body, setBody] = useState('')
  const [acknowledgementType, setAcknowledgementType] =
    useState<AcknowledgementType>('acknowledgement')
  const [statement, setStatement] = useState(t('defaultFileAckStatement'))
  const [dueDate, setDueDate] = useState<string>(() =>
    toDateString(moment().startOf('day').add(7, 'days'))
  )
  const [loading, setLoading] = useState(false)
  const { data: user } = useUser()
  const {
    selectedConversationIds,
    selectedRecipientIds,
    setSelectedRecipientIds,
    setSelectedConversationIds,
    selectedResourceId,
    selectedResourceType,
  } = useShareResourceContext()
  const { handleNavigation } = useRoutingContext()
  const { theme } = useTheme()

  const fileDetailsQuery = useFileDetails(
    selectedResourceId,
    selectedResourceType === 'file'
  )
  const folderDetailsQuery = useFolderDetails(
    selectedResourceId,
    selectedResourceType === 'folder'
  )

  const shareFileMutation = useShareFile()
  const shareFolderMutation = useShareFolder()

  const conversationsQuery = useConversations()
  const membersQuery = useRecipients()
  const conversationMembersQuery = useMembers(
    conversationId ?? '',
    !!conversationId && requestAcknowledgement
  )

  const members = useMemo(
    () =>
      requestAcknowledgement
        ? conversationMembersQuery.data
        : membersQuery.data?.filter(member => member.id !== user?.id),
    [
      conversationMembersQuery.data,
      membersQuery.data,
      requestAcknowledgement,
      user?.id,
    ]
  )

  useLayoutEffect(() => {
    const title = requestAcknowledgement ? t('send') : t('share')
    showHeaderTitle(navigation, title)
  }, [navigation, requestAcknowledgement])

  const onPressRemoveRecipient = useCallback(
    (recipient: ShareResourceRecipient) => {
      if (recipient.type === 'conversation') {
        setSelectedConversationIds(prev =>
          prev.filter(id => id !== recipient.data?.id)
        )
      } else {
        setSelectedRecipientIds(prev =>
          prev.filter(id => id !== recipient.data?.id)
        )
      }
    },
    [setSelectedConversationIds, setSelectedRecipientIds]
  )

  const onPressThumbnail = useCallback(() => {
    if (selectedResourceType === 'file' && selectedResourceId) {
      handleNavigation({
        routeName: 'file_viewer',
        routeParams: { fileId: selectedResourceId },
      })
    }
  }, [selectedResourceType, selectedResourceId, handleNavigation])

  const onSubmit = useCallback(async () => {
    setLoading(true)
    try {
      if (selectedResourceType === 'file' && selectedResourceId) {
        await shareFileMutation.mutateAsync({
          fileId: selectedResourceId,
          chatGroupIdList: selectedConversationIds,
          memberIdList: selectedRecipientIds,
          body,
          requireAcknowledgement: requestAcknowledgement,
          statement,
          requireSignature: acknowledgementType === 'signature',
          dueDate,
        })
      } else if (selectedResourceType === 'folder' && selectedResourceId) {
        await shareFolderMutation.mutateAsync({
          folderId: selectedResourceId,
          chatGroupIdList: selectedConversationIds,
          memberIdList: selectedRecipientIds,
          body,
        })
      }
      if (conversationId) {
        handleNavigation({
          routeName: 'file_folders_bottom_tab',
          routeParams: {
            screen: 'files',
            params: { conversationId: conversationId },
            conversationId: conversationId,
          },
        })
      } else {
        handleNavigation({
          routeName: 'conversations',
          routeParams: {
            screen:
              user?.role === 'observer' || user?.role === 'admin'
                ? 'groups'
                : 'allConversations',
          },
        })
      }
      setBody('')
      setSelectedConversationIds([])
      setSelectedRecipientIds([])
    } catch (error) {
      errorAlert(error)
    }
    setLoading(false)
  }, [
    selectedResourceType,
    selectedResourceId,
    handleNavigation,
    user?.role,
    setSelectedConversationIds,
    setSelectedRecipientIds,
    shareFileMutation,
    selectedConversationIds,
    selectedRecipientIds,
    body,
    requestAcknowledgement,
    statement,
    acknowledgementType,
    dueDate,
    shareFolderMutation,
  ])

  const selectedShareResourceRecipients: ShareResourceRecipient[] =
    useMemo(() => {
      const selectedConversations: ShareResourceRecipient[] =
        selectedConversationIds.reduce((acc, id) => {
          const conversation = conversationsQuery.data?.find(c => c.id === id)
          if (conversation) {
            return [...acc, { type: 'conversation', data: conversation }]
          }
          return acc
        }, [] as ShareResourceRecipient[])
      const selectedMembers: ShareResourceRecipient[] =
        selectedRecipientIds.reduce((acc, id) => {
          const member = members?.find(m => m.id === id)
          if (member) {
            return [...acc, { type: 'member', data: member }]
          }
          return acc
        }, [] as ShareResourceRecipient[])
      return [...selectedConversations, ...selectedMembers]
    }, [
      selectedConversationIds,
      selectedRecipientIds,
      conversationsQuery.data,
      members,
    ])

  const resourceDetailsQuery = useMemo(() => {
    if (selectedResourceType === 'file') {
      return fileDetailsQuery
    }
    return folderDetailsQuery
  }, [selectedResourceType, fileDetailsQuery, folderDetailsQuery])

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      onPress: onSubmit,
      title: t('submit'),
      loading,
      disabled: selectedShareResourceRecipients.length === 0,
      theme,
    })
  }, [
    navigation,
    onSubmit,
    theme,
    loading,
    selectedShareResourceRecipients.length,
  ])

  return (
    <StatusBoundary
      error={resourceDetailsQuery.error}
      isLoading={resourceDetailsQuery.isLoading}
    >
      <ShareResourceDetailsView
        resource={resourceDetailsQuery.data}
        selectedShareResourceRecipients={selectedShareResourceRecipients}
        onPressRemoveRecipient={onPressRemoveRecipient}
        onPressThumbnail={onPressThumbnail}
        body={body}
        setBody={setBody}
        acknowledgementType={acknowledgementType}
        setAcknowledgementType={setAcknowledgementType}
        statement={statement}
        setStatement={setStatement}
        requestAcknowledgement={requestAcknowledgement}
        dueDate={dueDate}
        setDueDate={setDueDate}
      />
    </StatusBoundary>
  )
}
